import { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import apiService from '../../../services/api';
import DetailPopup from '../../../components/DetailPopup';
import CompetitorAnalysisModal from '../../asis/components/CompetitorAnalysisModal';
import '../../asis/components/CompetitorAnalysisModal.css';
import { SEO_AS_IS_TABS } from '../../../constants/telemetryModules';
import { usePageTracking } from '../../../hooks/usePageTracking';
import { useTelemetry } from '../../../hooks/useTelemetry';
import { MODULES } from '../../../telemetry/constants/modules';
import { ACTIONS } from '../../../telemetry/constants/actions';

interface AsIsStateProps {
  onTooltipShow: (title: string, content: string) => void;
}

interface Parameter {
  name: string;
  status: string;
  statusType: 'optimal' | 'needs-attention' | 'critical';
  currentState: string;
  issue: string | null;
  description?: string;
  info?: string;
  key?: string; // Key to map to backend details
}

interface Category {
  id: string;
  name: string;
  icon: string;
  tooltip: string;
  parameters: Parameter[];
}

// Backend Group ID to Frontend Category ID Mapping
const GROUP_ID_MAPPING: Record<string, string> = {
  // OnPage (4 groups: serp_snippet, content_structure, accessibility, url_signals)
  // page_topic_keyword_targeting removed 2026-03-25 — will be re-added with multi-page logic
  'serp_snippet_optimization': 'serp-snippet',
  'content_structure_hierarchy': 'content-structure',
  'accessibility_optimization': 'accessibility',
  'url_page_signals': 'url-signals',

  // OffPage
  'domain_authority_trust': 'domain-authority',
  'backlink_relevance': 'backlink-relevance',
  'anchor_text_balance': 'anchor-text',
  'brand_entity_authority': 'brand-mentions',

  // Technical
  'crawl_indexation': 'crawl-indexation',
  'canonicalization_duplicate': 'canonicalization',
  'page_experience_performance': 'page-experience',
  'search_rendering_accessibility': 'rendering',
  'ai_llm_crawl': 'ai-llm'
};

const SERP_FEATURE_CATALOG: Array<{
  key: string;
  name: string;
  type: string;
  paid: boolean;
  icon: string;
  description: string;
}> = [
    { key: 'featured_snippet', name: 'Featured Snippet', type: 'Organic', paid: false, icon: 'ri-star-line', description: 'Direct answer box at the top of results.' },
    { key: 'ai_overview', name: 'AI Overview', type: 'Organic/Generative', paid: false, icon: 'ri-ai-generate', description: "Google's AI-generated summary answer." },
    { key: 'people_also_ask', name: 'People Also Ask', type: 'Organic', paid: false, icon: 'ri-question-line', description: 'Related expandable follow-up questions.' },
    { key: 'local_pack', name: 'Local Pack', type: 'Organic/Local', paid: false, icon: 'ri-map-pin-line', description: 'Map with local business listings.' },
    { key: 'knowledge_graph', name: 'Knowledge Panel', type: 'Organic/Entity', paid: false, icon: 'ri-book-line', description: 'Entity information panel shown on search results.' },
    { key: 'image_pack', name: 'Image Pack / Carousel', type: 'Organic', paid: false, icon: 'ri-image-line', description: 'Image-heavy visual SERP block.' },
    { key: 'video_results', name: 'Video Results / Carousel', type: 'Organic', paid: false, icon: 'ri-video-line', description: 'Video thumbnail result carousel.' },
    { key: 'top_stories', name: 'Top Stories / News Box', type: 'Organic/News', paid: false, icon: 'ri-newspaper-line', description: 'News-focused recent story cluster.' },
    { key: 'rich_snippets', name: 'Rich Snippets', type: 'Organic/Rich', paid: false, icon: 'ri-list-check-2', description: 'Enhanced snippets (ratings, prices, dates).' },
    { key: 'site_links', name: 'Sitelinks', type: 'Organic', paid: false, icon: 'ri-links-line', description: 'Sub-page links shown under primary result.' },
    { key: 'discussions_forums', name: 'Discussions & Forums', type: 'Organic/Community', paid: false, icon: 'ri-chat-3-line', description: 'Forum/community results like Reddit threads.' },
    { key: 'jobs_listing', name: 'Jobs Listing', type: 'Organic', paid: false, icon: 'ri-briefcase-line', description: 'Aggregated jobs block in SERP.' },
    { key: 'flights_hotels', name: 'Flights / Hotels Box', type: 'Organic/Vertical', paid: false, icon: 'ri-plane-line', description: 'Travel booking SERP integrations.' },
    { key: 'paid_ads', name: 'Paid Ads', type: 'Paid', paid: true, icon: 'ri-money-dollar-circle-line', description: 'Sponsored text ad placements.' },
    { key: 'shopping_results', name: 'Shopping Ads', type: 'Paid', paid: true, icon: 'ri-shopping-bag-line', description: 'Product listing ads with images/prices.' },
  ];

const SERP_CORE_SUMMARY_FEATURES = [
  'featured_snippet',
  'knowledge_graph',
  'local_pack',
  'image_pack',
  'video_results',
  'people_also_ask',
] as const;

const SERP_KEY_ALIASES: Record<string, string> = {
  knowledge_panel: 'knowledge_graph',
  knowledge_graph: 'knowledge_graph',
  image_carousel: 'image_pack',
  image_results: 'image_pack',
  image_pack: 'image_pack',
  video_carousel: 'video_results',
  video_results: 'video_results',
  video_pack: 'video_results',
  site_links: 'site_links',
  sitelinks: 'site_links',
  discussions_and_forums: 'discussions_forums',
  discussions_forums: 'discussions_forums',
  jobs_results: 'jobs_listing',
  jobs_listing: 'jobs_listing',
  flights: 'flights_hotels',
  hotels: 'flights_hotels',
  flights_hotels: 'flights_hotels',
  shopping_ads: 'shopping_results',
  shopping_results: 'shopping_results',
  paid_ads: 'paid_ads',
  ads: 'paid_ads',
};

const SERP_FEATURE_META = SERP_FEATURE_CATALOG.reduce((acc, item) => {
  acc[item.key] = item;
  return acc;
}, {} as Record<string, { key: string; name: string; type: string; paid: boolean; icon: string }>);

const SERP_TOTAL_FEATURES = SERP_FEATURE_CATALOG.length;

const humanizeSerpFeature = (key: string) =>
  key
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());

const canonicalizeSerpFeatureKey = (key: string): string => {
  const normalized = String(key || '').trim().toLowerCase();
  return SERP_KEY_ALIASES[normalized] || normalized;
};

const normalizeSerpFeatureKeys = (value: unknown): string[] => {
  if (!value) return [];
  if (Array.isArray(value)) {
    return value
      .map((v) => canonicalizeSerpFeatureKey(String(v || '').trim()))
      .filter(Boolean);
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return [];
    if (trimmed.startsWith('[')) {
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) {
          return parsed.map((v) => canonicalizeSerpFeatureKey(String(v || '').trim())).filter(Boolean);
        }
      } catch {
        // Fall back to CSV split below.
      }
    }
    return trimmed
      .split(',')
      .map((v) => canonicalizeSerpFeatureKey(v.trim()))
      .filter(Boolean);
  }
  return [];
};

export default function AsIsState({ onTooltipShow }: AsIsStateProps) {
  const [activeTab, setActiveTab] = useState('onpage');
  usePageTracking({
    moduleName: 'SEO Manager',
    submoduleName: `AS-IS State / ${SEO_AS_IS_TABS[activeTab] || activeTab}`,
    routePath: `/dashboard#as-is-state/${activeTab}`,
  });
  const [performanceFilter, setPerformanceFilter] = useState('full');
  const [expandedCategories, setExpandedCategories] = useState<string[]>([]);
  const { trackAction } = useTelemetry({ module: MODULES.SEO_MANAGER, submodule: 'As-Is State' });

  // State for dynamic data
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [loadingMessage, setLoadingMessage] = useState('Checking Database...');
  const [error, setError] = useState<string | null>(null);
  const [dynamicOnPage, setDynamicOnPage] = useState<any[]>([]);
  const [dynamicOffPage, setDynamicOffPage] = useState<any[]>([]);
  const [dynamicTechnical, setDynamicTechnical] = useState<any[]>([]);
  const [summaryData, setSummaryData] = useState<any>(null);
  const [summaryError, setSummaryError] = useState(false);
  const [trackedUniverseKeywords, setTrackedUniverseKeywords] = useState<string[]>([]);
  const [siteDomain, setSiteDomain] = useState<string>('');
  const [gscConnected, setGscConnected] = useState<boolean>(true);
  const [ga4Connected, setGa4Connected] = useState<boolean>(true);
  const [connectionsLoaded, setConnectionsLoaded] = useState<boolean>(false);

  // Popup state
  const [isKeywordPopupOpen, setIsKeywordPopupOpen] = useState(false);
  const [isBlogPopupOpen, setIsBlogPopupOpen] = useState(false);
  const [isSerpPopupOpen, setIsSerpPopupOpen] = useState(false);
  const [serpTab, setSerpTab] = useState<'features' | 'keywords'>('features'); // BRD: two tabs in SERP popup
  const [isCompetitorModalOpen, setIsCompetitorModalOpen] = useState(false);
  // Placeholder if needed for future use

  useEffect(() => {
    trackAction(ACTIONS.VIEW_AS_IS_STATE, 'Viewed AS-IS State');
    loadData();
  }, []);

  const loadData = async () => {
    let messageTimer: NodeJS.Timeout | null = null;

    try {
      setLoading(true);
      setConnectionsLoaded(false);
      setLoadingMessage('Checking Database...');

      // Simulate "DB Miss" -> "API Fetch" transition if it takes longer than 1.5s
      messageTimer = setTimeout(() => {
        setLoadingMessage('Fetching Live Data...');
      }, 1500);

      const userId = localStorage.getItem('user_id');
      if (!userId) { return; }

      // 1. Fetch initial settings (OAuth, User Profile, Keyword Universe) in parallel
      const [oauthRes, profileResult, universeResult] = await Promise.allSettled([
        apiService.checkOAuthStatus(userId),
        apiService.getUserProfile(userId),
        apiService.getKeywordUniverse(userId)
      ]);

      // Process OAuth Status
      if (oauthRes.status === 'fulfilled') {
        const oauthStatus = oauthRes.value;
        setGscConnected(Boolean(oauthStatus.gsc_connected));
        setGa4Connected(Boolean(oauthStatus.ga4_connected));
      } else {
        console.warn("Could not check oauth status", oauthRes.reason);
      }
      setConnectionsLoaded(true);

      // Process Profile
      let siteUrl = '';
      if (profileResult.status === 'fulfilled') {
        const profileData = profileResult.value?.data || profileResult.value;
        siteUrl = profileData?.website_url;
      }
      if (!siteUrl) {
        setLoading(false);
        return;
      }
      const domain = siteUrl.replace(/^https?:\/\//, '').replace(/\/$/, '');
      setSiteDomain(domain);

      // Process Keyword Universe
      let trackedKeywords: string[] = [];
      if (universeResult.status === 'fulfilled') {
        const universeData = universeResult.value?.data || universeResult.value;
        let items: any[] = [];
        if (universeData.keywords && Array.isArray(universeData.keywords)) {
          // V1 flat array
          items = universeData.keywords;
        } else if (universeData.keywords && typeof universeData.keywords === 'object') {
          // V2 format: keywords is { seo: [...], gmb: [...] }
          const kwObj = universeData.keywords as Record<string, any[]>;
          items = Object.values(kwObj).flat();
        } else if (universeData.items && Array.isArray(universeData.items)) {
          items = universeData.items;
        } else if (Array.isArray(universeData)) {
          items = universeData;
        }
        trackedKeywords = items
          .filter((item: any) => item.is_active || item.is_selected)
          .map((item: any) => item.keyword)
          .filter(Boolean);
        setTrackedUniverseKeywords(trackedKeywords);
      } else {
        console.warn("Could not fetch keyword universe", universeResult.reason);
        setTrackedUniverseKeywords([]);
      }

      // 2. Fetch Data for All Tabs & Summary
      const results = await Promise.allSettled([
        apiService.getAsIsParameters({ user_id: userId, site_url: siteUrl, tab: 'onpage', tracked_keywords: trackedKeywords }) as Promise<any>,
        apiService.getAsIsParameters({ user_id: userId, site_url: siteUrl, tab: 'offpage' }) as Promise<any>,
        apiService.getAsIsParameters({ user_id: userId, site_url: siteUrl, tab: 'technical', priority_urls: [siteUrl] }) as Promise<any>,
        apiService.getAsIsSummary({ user_id: userId, site_url: siteUrl, tracked_keywords: trackedKeywords }) as Promise<any>
      ]);

      const [onPageResult, offPageResult, technicalResult, summaryResult] = results;

      // 3. Update State with Robust Extraction
      const extractParams = (res: PromiseSettledResult<any>) => {
        if (res.status !== 'fulfilled' || !res.value) return null;
        const v = res.value;
        if (Array.isArray(v?.data?.parameters)) return v.data.parameters;
        if (Array.isArray(v?.data)) return v.data;
        if (Array.isArray(v?.parameters)) return v.parameters;
        if (Array.isArray(v)) return v;
        return null;
      };

      const onPageParams = extractParams(onPageResult);
      if (onPageParams && onPageParams.length > 0) {
        setDynamicOnPage(onPageParams);
      }

      const offPageParams = extractParams(offPageResult);
      if (offPageParams && offPageParams.length > 0) {
        setDynamicOffPage(offPageParams);
      }

      const techParams = extractParams(technicalResult);
      if (techParams && techParams.length > 0) {
        setDynamicTechnical(techParams);
      }

      if (summaryResult.status === 'fulfilled' && summaryResult.value) {
        const sumData = summaryResult.value?.data || summaryResult.value;
        if (sumData && (sumData.organic_traffic || sumData.keywords_performance || sumData.serp_features || sumData.competitor_rank)) {
          setSummaryData(sumData);
          setSummaryError(false);
        }
      } else if (summaryResult.status === 'rejected') {
        setSummaryError(true);
      }

      if (results.every(r => r.status === 'rejected')) {
        setError("Failed to load data from server.");
      } else {
        setError(null);
      }

    } catch (err) {
      console.error("Critical failure in AS-IS data loading", err);
      setError("Failed to load data from server.");
    } finally {
      if (messageTimer) clearTimeout(messageTimer);
      setConnectionsLoaded(true);
      setLoading(false);
    }
  };


  const handleKeywordCardClick = () => {
    setIsKeywordPopupOpen(true);
  };

  const handleBlogCardClick = () => {
    setIsBlogPopupOpen(true);
  };

  const handleSerpCardClick = () => {
    setIsSerpPopupOpen(true);
  };

  const handleCompetitorCardClick = () => {
    setIsCompetitorModalOpen(true);
  };

  // Baseline Metrics - More compact and precise
  const baselineMetrics = {
    keywordRank: {
      title: 'Keyword Rankings',
      icon: 'ri-key-line',
      color: 'from-teal-500 to-emerald-600',
      avgPosition: '#--',
      top10: 0,
      top20: 0,
      totalKeywords: 0,
      change: '-',
      top10Count: 0,
      topKeywords: [],
      tooltip: 'This shows where your website appears in search results for your target keywords. Higher rankings (especially top 10) drive significantly more organic traffic and visibility to your business.'
    },
    serpPositioning: {
      title: 'SERP Positioning',
      icon: 'ri-layout-grid-line',
      color: 'from-purple-500 to-purple-600',
      features: [],
      keywordDetails: [],
      tooltip: 'Shows your brand visibility in special search features beyond regular rankings. These features are crucial for capturing traffic and standing out in search results, even if you\'re not in the top 10 organic positions.'
    },
    organicTraffic: {
      title: 'Organic Traffic',
      icon: 'ri-user-line',
      color: 'from-green-500 to-green-600',
      totalClicks: 0,
      totalImpressions: 0,
      // avgSessionDuration intentionally absent — GA4 not integrated.
      // TODO: Replace with GA4 avgEngagementTime when GA4 integration is implemented.
      // See BRD section 2.3.
      topPages: [] as Array<{ page: string; visitors: number }>,
      data_available: false,
      tooltip: 'Measures visitors who find your site through unpaid search results and how often your site appears in search results (impressions). This represents the direct value of your SEO efforts and indicates how visible your business is to potential customers searching online.'
    },
    competitorBenchmark: {
      title: 'Competitive Position',
      icon: 'ri-bar-chart-box-line',
      color: 'from-orange-500 to-orange-600',
      yourScore: 0,
      yourRank: 0,
      totalCompetitors: 0,
      competitors: [],
      tooltip: 'Compares your overall SEO performance against top competitors in your industry. Understanding your competitive position helps identify opportunities to outrank competitors and capture more market share in search results.'
    }
  };

  // Derived Metrics from API Data
  // Include ALL keywords returned by the backend (ranked + unranked '>100').
  // Normalize any GMB "near me" keywords to 'N/A' and sort:
  // 1) Ranked keywords (1, 2, 3...) ascending
  // 2) Unranked keywords (>100)
  // 3) 'near me' / 'N/A' keywords at the very bottom
  const rawRankingKeywords = summaryData?.keywords_performance?.ranked_keywords || [];
  const featuredRankingKeywords = [...rawRankingKeywords]
    .map((kwItem: any) => {
      if (typeof kwItem === 'object' && kwItem !== null && String(kwItem.keyword || '').toLowerCase().includes('near me')) {
        return {
          ...kwItem,
          position: 'N/A',
          isTop: false,
        };
      }
      return kwItem;
    })
    .sort((a: any, b: any) => {
      const getSortWeight = (item: any) => {
        const kwText = String(item?.keyword || '').toLowerCase();
        if (kwText.includes('near me') || item?.position === 'N/A' || item?.position === 'NA') {
          return 2000000; // Bottom-most
        }
        if (item?.position === '>100') {
          return 1000000; // Unranked in middle
        }
        const num = parseFloat(item?.position);
        if (!isNaN(num) && num > 0) {
          return num; // Ranked ascending
        }
        return 1000000;
      };
      const diff = getSortWeight(a) - getSortWeight(b);
      if (diff !== 0) return diff;
      return String(a?.keyword || '').localeCompare(String(b?.keyword || ''));
    });

  const keywordMetrics = summaryData?.keywords_performance ? {
    ...baselineMetrics.keywordRank,
    // Show "#--" when no keywords are actively ranking (avg_position === 0)
    avgPosition: summaryData.keywords_performance.avg_position > 0
      ? `#${summaryData.keywords_performance.avg_position.toFixed(1)}`
      : '#--',
    totalKeywords: summaryData.keywords_performance.tracked_keywords_count || 0,
    topKeywords: featuredRankingKeywords
  } : baselineMetrics.keywordRank;

  const formatAvgDuration = (seconds?: number | null) => {
    if (!seconds || Number.isNaN(seconds) || seconds <= 0) return 'N/A';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    if (mins <= 0) return `${secs}s`;
    return `${mins}m ${secs}s`;
  };

  const rawAvgDurationSeconds = Number(
    summaryData?.organic_traffic?.avg_duration_seconds
    ?? summaryData?.organic_traffic?.average_duration_seconds
    ?? summaryData?.organic_traffic?.avg_session_duration_seconds
    ?? summaryData?.organic_traffic?.average_session_duration_seconds
    ?? summaryData?.organic_traffic?.avg_engagement_time_seconds
    ?? summaryData?.organic_traffic?.average_engagement_time_seconds
    ?? 0
  );

  const hasTrafficData = Boolean(
    summaryData?.organic_traffic?.data_available || rawAvgDurationSeconds > 0
  );

  const trafficMetrics = hasTrafficData ? {
    ...baselineMetrics.organicTraffic,
    totalClicks: summaryData.organic_traffic.total_clicks || 0,
    totalImpressions: summaryData.organic_traffic.total_impressions || 0,
    topPages: (summaryData.organic_traffic.top_pages || []) as Array<{ page: string; visitors: number }>,
    avgDuration: formatAvgDuration(rawAvgDurationSeconds),
    data_available: true,
  } : baselineMetrics.organicTraffic;


  // SERP rules:
  // - Summary card uses the 6 core features.
  // - Active: brand appears in that feature for at least one tracked keyword.
  // - Missing: feature appears in SERP for tracked keywords, but brand is absent.
  // - Not Applicable: feature never appeared for any tracked keyword SERP.
  // - Popup lists all 15 tracked features (organic + paid).
  const serpMetrics = summaryData?.serp_features ? {
    ...baselineMetrics.serpPositioning,
    // Prefer the backend feature_status_map (new enriched path)
    // Fall back to the legacy inferred path for old cache rows.
    features: (() => {
      const featureStatusMap: Record<string, string> =
        summaryData.serp_features.feature_status_map || {};
      const hasStatusMap = Object.keys(featureStatusMap).length > 0;

      if (hasStatusMap) {
        // New enriched path: use backend classification directly
        return SERP_FEATURE_CATALOG.map((catalogItem) => {
          const key = catalogItem.key;
          const status = featureStatusMap[key] || 'not_applicable';
          const meta = SERP_FEATURE_META[key];
          return {
            key,
            name: meta?.name || humanizeSerpFeature(key),
            type: meta?.type || 'Organic',
            paid: meta?.paid || false,
            icon: meta?.icon || 'ri-layout-grid-line',
            description: catalogItem.description || meta?.description || '',
            appearsInSerp: status !== 'not_applicable',
            present: status === 'active',
            missing: status === 'missing',
            notApplicable: status === 'not_applicable',
            activeCount: status === 'active' ? 1 : 0,
            status,
          };
        });
      }

      // Legacy path: infer from target_feature_presence + features_present
      const rawTargetPresence = summaryData.serp_features.target_feature_presence || {};
      const targetPresence = Object.entries(rawTargetPresence).reduce((acc, [rawKey, rawCount]) => {
        const normalizedKey = canonicalizeSerpFeatureKey(rawKey);
        const count = Number(rawCount || 0);
        if (!normalizedKey || !Number.isFinite(count) || count <= 0) return acc;
        acc[normalizedKey] = (acc[normalizedKey] || 0) + count;
        return acc;
      }, {} as Record<string, number>);

      const featuresPresent = normalizeSerpFeatureKeys(summaryData.serp_features.features_present);
      const apiFeatureKeys = new Set<string>([
        ...featuresPresent,
        ...Object.keys(targetPresence).map(canonicalizeSerpFeatureKey),
        ...((summaryData.serp_features.keyword_details || []).flatMap((row: any) => normalizeSerpFeatureKeys(row.features || []))),
        ...((summaryData.serp_features.keyword_details || []).flatMap((row: any) => normalizeSerpFeatureKeys(row.your_presence || []))),
      ]);

      return SERP_FEATURE_CATALOG.map((catalogItem) => {
        const key = catalogItem.key;
        const appearsInSerp = apiFeatureKeys.has(key);
        const activeCount = Number(targetPresence[key] || 0);
        const present = activeCount > 0;
        const missing = appearsInSerp && !present;
        const meta = SERP_FEATURE_META[key];
        return {
          key,
          name: meta?.name || humanizeSerpFeature(key),
          type: meta?.type || 'Organic',
          paid: meta?.paid || false,
          icon: meta?.icon || 'ri-layout-grid-line',
          description: catalogItem.description || meta?.description || '',
          appearsInSerp,
          present,
          missing,
          notApplicable: !appearsInSerp,
          activeCount,
          status: present ? 'active' : missing ? 'missing' : 'not_applicable',
        };
      });
    })(),
    keywordDetails: (() => {
      const details = summaryData.serp_features.keyword_details || [];
      const returnedKws = new Set(details.map((d: any) => d.keyword?.toLowerCase()));
      const padded = [...details];
      for (const kw of trackedUniverseKeywords) {
        if (kw && !returnedKws.has(kw.toLowerCase())) {
          padded.push({
            keyword: kw,
            features: [],
            your_presence: [],
            position: null,
            is_top10: false,
          });
        }
      }
      return padded;
    })(),
    // BRD §4: prefer backend-computed counts when available
    applicableCount: Number(summaryData.serp_features.applicable_count ?? summaryData.serp_features.features_count ?? 0),
    activeCount: Number(summaryData.serp_features.active_count ?? summaryData.serp_features.domain_in_features ?? 0),
    notApplicableCount: Number(summaryData.serp_features.not_applicable_count ?? 0),
    featuresCountRaw: Number(summaryData.serp_features.features_count || 0),
    domainInFeaturesRaw: Number(summaryData.serp_features.domain_in_features || 0),
    baselineFeaturesCount: summaryData.serp_features.baseline_features_count ?? null,
    pipelineError: Boolean(summaryData.serp_features.pipeline_error),
    data_available: summaryData.serp_features.data_available ?? false,
  } : {
    ...baselineMetrics.serpPositioning,
    features: SERP_FEATURE_CATALOG.map((meta) => ({
      key: meta.key,
      name: meta.name,
      type: meta.type,
      paid: meta.paid,
      icon: meta.icon,
      description: meta.description,
      appearsInSerp: false,
      present: false,
      missing: false,
      notApplicable: true,
      activeCount: 0,
      status: 'not_applicable',
    })),
    keywordDetails: trackedUniverseKeywords.map((kw) => ({
      keyword: kw,
      features: [],
      your_presence: [],
      position: null,
      is_top10: false,
    })),
    applicableCount: 0,
    activeCount: 0,
    notApplicableCount: SERP_FEATURE_CATALOG.length,
    featuresCountRaw: 0,
    domainInFeaturesRaw: 0,
    baselineFeaturesCount: null,
    pipelineError: false,
    data_available: false,
  };

  const serpSummaryFeatures = serpMetrics.features.filter((f: any) =>
    (SERP_CORE_SUMMARY_FEATURES as readonly string[]).includes(f.key)
  );

  // BRD §4: use backend-computed counts when available; fall back to derived counts
  const serpActiveCount = serpMetrics.activeCount ?? serpMetrics.features.filter((f: any) => f.present).length;
  const serpMissingCount = serpMetrics.features.filter((f: any) => f.missing).length;
  const serpApplicableCount = serpMetrics.applicableCount ?? (serpActiveCount + serpMissingCount);
  const serpNotApplicableCount = serpMetrics.notApplicableCount ?? Math.max(SERP_TOTAL_FEATURES - serpApplicableCount, 0);

  // On-Page SEO Parameters — 4 groups only.
  // page-topic (page_topic_keyword_targeting) removed 2026-03-25.
  // Will be re-added with multi-page multi-URL keyword presence logic.
  const initialOnPageCategories: Category[] = [
    {
      id: 'serp-snippet',
      name: 'SERP Snippet Optimization',
      icon: 'ri-search-eye-line',
      tooltip: 'Measures how your pages appear in search results (CTR).',
      parameters: [
        { name: 'Title tag presence & optimization', status: '-', statusType: 'needs-attention', currentState: '-', issue: null, key: 'title_tag_presence_optimization' },
        { name: 'Meta description presence & quality', status: '-', statusType: 'needs-attention', currentState: '-', issue: null, key: 'meta_description_presence_quality' },
        { name: 'Title/meta duplication', status: '-', statusType: 'needs-attention', currentState: '-', issue: null, key: 'title_meta_duplication' },
        { name: 'Pixel length issues', status: '-', statusType: 'needs-attention', currentState: '-', issue: null, key: 'pixel_length_issues' },
        { name: 'CTR readiness signals', status: '-', statusType: 'needs-attention', currentState: '-', issue: null, key: 'ctr_readiness_signals' }
      ]
    },
    {
      id: 'content-structure',
      name: 'Content Structure & Semantic Hierarchy',
      icon: 'ri-list-ordered',
      tooltip: 'Measures content organization and heading structure.',
      parameters: [
        { name: 'H1 presence & uniqueness', status: '-', statusType: 'needs-attention', currentState: '-', issue: null, key: 'h1_presence_uniqueness' },
        { name: 'H2–H6 hierarchy', status: '-', statusType: 'needs-attention', currentState: '-', issue: null, key: 'h2_h6_hierarchy' },
        { name: 'Heading order issues', status: '-', statusType: 'needs-attention', currentState: '-', issue: null, key: 'heading_order_issues' },
        { name: 'First 100 words relevance', status: '-', statusType: 'needs-attention', currentState: '-', issue: null, key: 'first_100_words_relevance' },
        { name: 'Content sectioning', status: '-', statusType: 'needs-attention', currentState: '-', issue: null, key: 'content_sectioning' }
      ]
    },
    {
      id: 'accessibility',
      name: 'Accessibility Optimization',
      icon: 'ri-eye-line',
      tooltip: 'Measures image accessibility and SEO.',
      parameters: [
        { name: 'Image alt text', status: '-', statusType: 'needs-attention', currentState: '-', issue: null, key: 'image_alt_text' },
        { name: 'Image file naming', status: '-', statusType: 'needs-attention', currentState: '-', issue: null, key: 'image_file_naming' },
        { name: 'Image relevance', status: '-', statusType: 'needs-attention', currentState: '-', issue: null, key: 'image_relevance' },
        { name: 'Lazy loading issues', status: '-', statusType: 'needs-attention', currentState: '-', issue: null, key: 'lazy_loading_issues' },
        { name: 'Accessibility compliance (basic)', status: '-', statusType: 'needs-attention', currentState: '-', issue: null, key: 'accessibility_compliance' }
      ]
    },
    {
      id: 'url-signals',
      name: 'URL & Page Level Signals',
      icon: 'ri-link',
      tooltip: 'Measures URL structure and page-level technical signals.',
      parameters: [
        { name: 'URL structure', status: '-', statusType: 'needs-attention', currentState: '-', issue: null, key: 'url_structure' },
        { name: 'URL length', status: '-', statusType: 'needs-attention', currentState: '-', issue: null, key: 'url_length' },
        { name: 'Keyword in URL', status: '-', statusType: 'needs-attention', currentState: '-', issue: null, key: 'keyword_in_url' },
        { name: 'Parameterized URLs', status: '-', statusType: 'needs-attention', currentState: '-', issue: null, key: 'parameterized_urls' },
        { name: 'Duplicate URLs', status: '-', statusType: 'needs-attention', currentState: '-', issue: null, key: 'duplicate_urls' },
        { name: 'Canonical alignment', status: '-', statusType: 'needs-attention', currentState: '-', issue: null, key: 'canonical_alignment' }
      ]
    }
  ];

  // Off-Page SEO Parameters - Restructured with status only
  const initialOffPageCategories: Category[] = [
    {
      id: 'domain-authority',
      name: 'Domain Authority & Trust Signals',
      icon: 'ri-shield-check-line',
      tooltip: 'Measures overall domain authority and trustworthiness.',
      parameters: [
        { name: 'Referring domains count', status: 'Needs Attention', statusType: 'needs-attention', currentState: 'Limited', issue: null, key: 'referring_domains_count' },
        { name: 'Authority score', status: 'Needs Attention', statusType: 'needs-attention', currentState: 'Moderate', issue: null, key: 'authority_score' },
        { name: 'Spam Score', status: 'Optimal', statusType: 'optimal', currentState: 'Low', issue: null, key: 'spam_score' },
        { name: 'Follow vs nofollow backlinks', status: 'Optimal', statusType: 'optimal', currentState: 'Balanced', issue: null, key: 'follow_vs_nofollow_backlinks' },
        { name: 'Link source quality distribution', status: 'Needs Attention', statusType: 'needs-attention', currentState: 'Limited', issue: null, key: 'link_source_quality_distribution' }
      ]
    },
    {
      id: 'backlink-relevance',
      name: 'Backlink Relevance & Topic Alignment',
      icon: 'ri-links-line',
      tooltip: 'Measures how relevant your backlinks are to your business and content topics.',
      parameters: [
        { name: 'Toxic Score', status: 'Optimal', statusType: 'optimal', currentState: 'Low', issue: null, key: 'toxic_score' }
      ]
    },
    {
      id: 'anchor-text',
      name: 'Anchor Text Profile & Risk Balance',
      icon: 'ri-text',
      tooltip: 'Measures anchor text distribution and over-optimization risks.',
      parameters: [
        { name: 'Anchor Text Distribution', status: 'Optimal', statusType: 'optimal', currentState: 'Natural', issue: null, key: 'anchor_text_distribution' },
        { name: 'Exact-match anchor frequency', status: 'Optimal', statusType: 'optimal', currentState: 'Safe levels', issue: null, key: 'exact_match_anchor_frequency' }
      ]
    },
    {
      id: 'brand-mentions',
      name: 'Brand Mentions & Entity Authority',
      icon: 'ri-megaphone-line',
      tooltip: 'Measures brand visibility and entity recognition across the web.',
      parameters: [
        { name: 'Brand citations', status: 'Optimal', statusType: 'optimal', currentState: 'Consistent', issue: null, key: 'brand_citations' },
        { name: 'Consistency of brand name usage', status: 'Optimal', statusType: 'optimal', currentState: 'Consistent', issue: null, key: 'consistency_of_brand_name_usage' },
        { name: 'Industry mentions', status: 'Needs Attention', statusType: 'needs-attention', currentState: 'Limited', issue: null, key: 'industry_mentions' }
      ]
    }
  ];

  // Technical SEO Parameters - BRD Compliant Structure
  const initialTechnicalCategories: Category[] = [
    {
      id: 'crawl-indexation',
      name: 'Crawl & Indexation',
      icon: 'ri-search-line',
      tooltip: 'Measures how search engines discover and index your pages.',
      parameters: [
        { name: 'XML Sitemap Health', status: 'Pending', statusType: 'needs-attention', currentState: 'Checking...', issue: null, key: 'xml_sitemap_health' },
        { name: 'Robots.txt Configuration', status: 'Pending', statusType: 'needs-attention', currentState: 'Checking...', issue: null, key: 'robots_txt_configuration' },
        { name: 'Indexed vs Non-Indexed Pages', status: 'Pending', statusType: 'needs-attention', currentState: 'Checking...', issue: null, key: 'indexed_vs_non_indexed_pages' },
        { name: 'Crawl Budget Efficiency', status: 'Pending', statusType: 'needs-attention', currentState: 'Checking...', issue: null, key: 'crawl_budget_efficiency' },
        { name: 'Orphan URLs', status: 'Pending', statusType: 'needs-attention', currentState: 'Checking...', issue: null, key: 'orphan_urls' }
      ]
    },
    {
      id: 'canonicalization',
      name: 'Canonicalization & Duplicate Control',
      icon: 'ri-file-copy-line',
      tooltip: 'Measures duplicate content management and canonicalization accuracy.',
      parameters: [

        { name: 'Canonical Tag Implementation', status: '-', statusType: 'needs-attention', currentState: '-', issue: null, key: 'canonical_tag_implementation' },
        { name: 'Duplicate URLs', status: '-', statusType: 'needs-attention', currentState: '-', issue: null, key: 'duplicate_urls' },
        { name: 'HTTP vs HTTPS Duplicates', status: '-', statusType: 'needs-attention', currentState: '-', issue: null, key: 'http_vs_https_duplicates' },
        { name: 'Trailing Slash Issues', status: '-', statusType: 'needs-attention', currentState: '-', issue: null, key: 'trailing_slash_issues' }
      ]
    },
    {
      id: 'page-experience',
      name: 'Page Experience Performance',
      icon: 'ri-speed-line',
      tooltip: 'Measures Core Web Vitals and performance metrics.',
      parameters: [
        { name: 'LCP Performance', status: '-', statusType: 'needs-attention', currentState: '-', issue: null, key: 'lcp_performance' },
        { name: 'CLS Stability', status: '-', statusType: 'needs-attention', currentState: '-', issue: null, key: 'cls_stability' },
        { name: 'Mobile vs Desktop CWV', status: '-', statusType: 'needs-attention', currentState: '-', issue: null, key: 'mobile_vs_desktop_cwv' }
      ]
    },
    {
      id: 'rendering',
      name: 'Search Rendering Accessibility',
      icon: 'ri-code-box-line',
      tooltip: 'Measures how search engines render and access your content.',
      parameters: [
        { name: 'JavaScript Rendering Issues', status: '-', statusType: 'needs-attention', currentState: '-', issue: null, key: 'javascript_rendering_issues' },
        { name: 'Delayed/Lazy Content', status: '-', statusType: 'needs-attention', currentState: '-', issue: null, key: 'delayed_lazy_content' },
        { name: 'Hidden Content', status: '-', statusType: 'needs-attention', currentState: '-', issue: null, key: 'hidden_content' },
        { name: 'Client-side Rendering Risks', status: '-', statusType: 'needs-attention', currentState: '-', issue: null, key: 'client_side_rendering_risks' },
        { name: 'Critical Resource Blocking', status: '-', statusType: 'needs-attention', currentState: '-', issue: null, key: 'critical_resource_blocking' }
      ]
    },
    {
      id: 'ai-llm',
      name: 'AI & LLM Crawl',
      icon: 'ri-robot-line',
      tooltip: 'Measures optimization for AI systems and LLM crawlers.',
      parameters: [
        { name: 'llm.txt Presence & Rules', status: '-', statusType: 'needs-attention', currentState: '-', issue: null, key: 'llm_txt_presence_rules' },
        { name: 'AI Crawler Permissions', status: '-', statusType: 'needs-attention', currentState: '-', issue: null, key: 'ai_crawler_permissions' },
        { name: 'Content Usage Signals', status: '-', statusType: 'needs-attention', currentState: '-', issue: null, key: 'content_usage_signals' },
        { name: 'AI Indexing Exposure', status: '-', statusType: 'needs-attention', currentState: '-', issue: null, key: 'ai_indexing_exposure' }
      ]
    }
  ];

  const toggleCategory = (categoryId: string) => {
    setExpandedCategories(prev =>
      prev.includes(categoryId)
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  const getFilteredCategories = (categories: typeof onPageCategories) => {
    return categories.map(category => ({
      ...category,
      parameters: category.parameters.filter((param: any) => {
        if (performanceFilter === 'optimal') return param.statusType === 'optimal';
        if (performanceFilter === 'needs-attention') return param.statusType === 'needs-attention';
        return true;
      })
    })).filter(category => category.parameters.length > 0);
  };

  const getFilteredTechnicalCategories = (categories: typeof technicalCategories) => {
    return categories.map(category => ({
      ...category,
      parameters: category.parameters.filter((param: any) => {
        if (performanceFilter === 'optimal') return param.statusType === 'optimal';
        if (performanceFilter === 'needs-attention') return param.statusType === 'needs-attention';
        return true;
      })
    })).filter(category => category.parameters.length > 0);
  };

  const getCategoryStatus = (parameters: Parameter[]) => {
    if (!parameters || parameters.length === 0) return 'optimal';

    const counts = {
      'optimal': 0,
      'needs-attention': 0,
      'critical': 0
    };

    parameters.forEach(p => {
      const type = p.statusType;
      if (type === 'optimal' || type === 'needs-attention' || type === 'critical') {
        counts[type]++;
      }
    });

    const statuses: ('optimal' | 'needs-attention' | 'critical')[] = ['optimal', 'needs-attention', 'critical'];
    let bestStatus = statuses[0];
    let maxVal = counts[bestStatus];

    for (let i = 1; i < statuses.length; i++) {
      const curStatus = statuses[i];
      const curCount = counts[curStatus];
      if (curCount >= maxVal) {
        bestStatus = curStatus;
        maxVal = curCount;
      }
    }
    return bestStatus;
  };

  const getTechnicalCategoryStatus = (parameters: Parameter[]) => getCategoryStatus(parameters);



  // MERGE LOGIC
  const mergeData = (initialCats: any[], apiParams: any[]) => {
    // Create a map of GroupID -> ApiParam
    const paramMap = new Map();
    apiParams.forEach(p => paramMap.set(p.group_id, p));

    return initialCats.map(cat => {
      const backendId = Object.keys(GROUP_ID_MAPPING).find(key => GROUP_ID_MAPPING[key] === cat.id);

      if (backendId && paramMap.has(backendId)) {
        const apiData = paramMap.get(backendId);

        // Normalise details: backend may send a JSON string (locked baseline path) or a plain object
        let parsedDetails: Record<string, any> = {};
        if (apiData.details && typeof apiData.details === 'object') {
          parsedDetails = apiData.details;
        } else if (typeof apiData.details === 'string') {
          try {
            const d = JSON.parse(apiData.details);
            parsedDetails = (d && typeof d.details === 'object') ? d.details : d;
          } catch { /* leave parsedDetails empty */ }
        }

        // Update category status based on apiData.status
        const newParams = cat.parameters.map((p: any) => {
          let pStatus = apiData.status; // Default to group status
          let pCurrentState = apiData.status === 'optimal' ? 'Pass' : 'Issues detected';
          let pIssue = apiData.status === 'needs_attention' || apiData.status === 'critical' ? 'Optimization opportunity' : null;

          // If we have granular details for this parameter
          if (p.key && parsedDetails[p.key]) {
            const detail = parsedDetails[p.key];
            pStatus = detail.status;
            pCurrentState = detail.value;
            // Map status to issue text if not optimal
            if (pStatus !== 'optimal') {
              pIssue = detail.value; // Use the specific finding as the issue text
            } else {
              pIssue = null;
            }
          }

          return {
            ...p,
            status: pStatus === 'optimal' ? 'Optimal' : pStatus === 'critical' ? 'Critical' : 'Needs Attention',
            statusType: pStatus === 'optimal' ? 'optimal' : pStatus === 'critical' ? 'critical' : 'needs-attention',
            currentState: pCurrentState,
            issue: pIssue
          };
        });

        return { ...cat, parameters: newParams };
      }
      return cat;
    });
  };

  const onPageCategories = useMemo(() => mergeData(initialOnPageCategories, dynamicOnPage), [initialOnPageCategories, dynamicOnPage]);
  const offPageCategories = useMemo(() => mergeData(initialOffPageCategories, dynamicOffPage), [initialOffPageCategories, dynamicOffPage]);
  const technicalCategories = useMemo(() => mergeData(initialTechnicalCategories, dynamicTechnical), [initialTechnicalCategories, dynamicTechnical]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">As-Is Baseline Assessment</h2>
            {loading && (
              <span className="ml-4 text-sm text-blue-500 font-medium animate-pulse flex items-center">
                <i className="ri-loader-4-line animate-spin mr-1"></i>
                {loadingMessage}
              </span>
            )}
            {error && <span className="ml-4 text-sm text-red-500 font-medium flex items-center"><i className="ri-error-warning-line mr-1"></i> {error}</span>}
            {!loading && summaryData && (
              <span className={`ml-4 text-xs font-medium flex items-center ${summaryData.sync_source === 'database' ? 'text-green-600' : 'text-blue-600'
                }`}>
                <i className={`${summaryData.sync_source === 'database' ? 'ri-database-2-line' : 'ri-cloud-line'} mr-1`}></i>
                {summaryData.sync_message || (summaryData.from_cache ? 'Loaded from database' : 'Synced from APIs')}
              </span>
            )}
          </div>
          <button
            onClick={() => onTooltipShow('As-Is Baseline', 'This is a comprehensive snapshot of your current SEO performance. It shows where you stand today across all critical areas, helping you understand what needs improvement and track progress over time.')}
            className="text-gray-400 hover:text-blue-500"
          >
            <i className="ri-information-line text-xl"></i>
          </button>
        </div>
        <p className="text-gray-600 dark:text-gray-400">Current state analysis capturing your SEO health at onboarding</p>
      </div>

      {/* Compact Baseline Metrics Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 gap-4">
        {/* Keyword Rankings Card */}
        <div
          onClick={handleKeywordCardClick}
          className="bg-white dark:bg-gray-800 rounded-2xl p-5 shadow-sm border border-gray-100 dark:border-gray-700 cursor-pointer hover:shadow-md transition-shadow group"
        >
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 rounded-xl bg-teal-500 flex items-center justify-center group-hover:scale-110 transition-transform">
                <span className="text-white font-bold text-lg">O<sub className="text-xs">IT</sub></span>
              </div>
              <div>
                <h3 className="text-base font-bold text-gray-900 dark:text-white">Keyword<br />Rankings</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {!connectionsLoaded || (loading && !summaryError)
                    ? 'Loading…'
                    : gscConnected
                    ? `${keywordMetrics.totalKeywords} keywords`
                    : 'GSC not connected'}
                </p>
              </div>
            </div>
            <button
              onClick={() => onTooltipShow(keywordMetrics.title, keywordMetrics.tooltip)}
              className="text-gray-400 hover:text-blue-500"
            >
              <i className="ri-information-line text-sm"></i>
            </button>
          </div>

          {!connectionsLoaded || (loading && !summaryError) ? (
            <div className="flex flex-col items-center justify-center py-6 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500 mb-2"></div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Loading data…</p>
            </div>
          ) : !gscConnected ? (
            <div className="flex flex-col items-center justify-center py-6 text-center">
              <i className="ri-key-line text-3xl text-gray-300 dark:text-gray-600 mb-2"></i>
              <p className="text-sm text-gray-500 dark:text-gray-400">Connect GSC to fetch the data</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="text-center">
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{keywordMetrics.avgPosition}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Avg Pos</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">{keywordMetrics.totalKeywords}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Tracked</p>
                </div>
              </div>

              <div className="space-y-2">
                {summaryError ? (
                  <div className="flex flex-col items-center justify-center py-6 text-center">
                    <i className="ri-error-warning-line text-3xl text-red-400 mb-2"></i>
                    <p className="text-sm text-red-500 font-medium">Failed to load data from server</p>
                  </div>
                ) : keywordMetrics.topKeywords.length > 0 ? (
                  keywordMetrics.topKeywords.slice(0, 5).map((kw: any, idx: number) => {
                    const isNearMe = String(kw.keyword || '').toLowerCase().includes('near me');
                    const isNA = isNearMe || kw.position === 'N/A' || kw.position === 'NA';
                    return (
                      <div key={idx} className="flex items-center justify-between text-sm">
                        <span className="text-gray-900 dark:text-white truncate flex-1" title={kw.keyword}>{kw.keyword}</span>
                        <div className="flex items-center space-x-2 ml-2">
                          <span className={`font-bold ${isNA ? 'text-gray-400' : kw.isTop ? 'text-teal-600' : 'text-gray-400'}`}>
                            {isNA ? 'N/A' : /^\d+(\.\d+)?$/.test(String(kw.position)) ? `#${kw.position}` : kw.position}
                          </span>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="text-xs text-gray-400 italic text-center py-2">Loading keywords…</div>
                )}
              </div>
            </>
          )}
        </div>

        {/* SERP Positioning Card */}
        <div
          onClick={handleSerpCardClick}
          className="bg-white dark:bg-gray-800 rounded-2xl p-5 shadow-sm border border-gray-100 dark:border-gray-700 cursor-pointer hover:shadow-md transition-shadow group"
        >
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 rounded-xl bg-purple-500 flex items-center justify-center group-hover:scale-110 transition-transform">
                <i className="ri-layout-grid-line text-white text-xl"></i>
              </div>
              <div>
                <h3 className="text-base font-bold text-gray-900 dark:text-white">Captured<br />Visibility</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {serpMetrics.data_available
                    ? `Applicable: ${serpApplicableCount}`
                    : 'SERP features'}
                </p>
              </div>
            </div>
            <button
              onClick={() => onTooltipShow(serpMetrics.title, serpMetrics.tooltip)}
              className="text-gray-400 hover:text-blue-500"
            >
              <i className="ri-information-line text-sm"></i>
            </button>
          </div>

          <div className="grid grid-cols-3 gap-3 mb-4">
            <div className="text-center">
              <p className="text-2xl font-bold text-teal-600">
                {serpActiveCount}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Active</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-gray-400">
                {serpMissingCount}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Missing</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-purple-600">
                {serpNotApplicableCount}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">N/A</p>
            </div>
          </div>
          <div className="space-y-2">
            {summaryError ? (
              <div className="flex flex-col items-center justify-center py-6 text-center">
                <i className="ri-error-warning-line text-3xl text-red-400 mb-2"></i>
                <p className="text-sm text-red-500 font-medium">Failed to load data from server</p>
              </div>
            ) : (
              <>
                {serpSummaryFeatures.map((feature: any, idx: number) => (
                  <div key={idx} className="flex items-center justify-between text-sm">
                    <span className="text-gray-900 dark:text-white">{feature.name}</span>
                    <div className={`w-5 h-5 rounded flex items-center justify-center ${feature.present ? 'bg-teal-100' : feature.missing ? 'bg-amber-100' : 'bg-gray-100'
                      }`}>
                      {feature.present ? (
                        <i className="ri-check-line text-teal-600 text-sm"></i>
                      ) : feature.missing ? (
                        <i className="ri-close-line text-amber-600 text-sm"></i>
                      ) : (
                        <i className="ri-subtract-line text-gray-400 text-sm"></i>
                      )}
                    </div>
                  </div>
                ))}
                {!serpMetrics.data_available && (
                  <p className="text-[10px] text-gray-400 italic text-center pt-1">
                    SERP features will populate once ranking data is available
                  </p>
                )}
              </>
            )}
          </div>
        </div>

        {/* Organic Traffic Card */}
        <div
          onClick={handleBlogCardClick}
          className="bg-white dark:bg-gray-800 rounded-2xl p-5 shadow-sm border border-gray-100 dark:border-gray-700 cursor-pointer hover:shadow-md transition-shadow group"
        >
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 rounded-xl bg-teal-500 flex items-center justify-center group-hover:scale-110 transition-transform">
                <i className="ri-user-line text-white text-xl"></i>
              </div>
              <div>
                <h3 className="text-base font-bold text-gray-900 dark:text-white">Organic<br />Traffic</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {!connectionsLoaded || (loading && !summaryError)
                    ? 'Loading…'
                    : (!gscConnected && !ga4Connected)
                    ? 'GA & GSC not connected'
                    : (gscConnected && !ga4Connected)
                    ? 'GSC Connected • GA Not Connected'
                    : (!gscConnected && ga4Connected)
                    ? 'GA Connected • GSC Not Connected'
                    : 'Average duration'}
                </p>
              </div>
            </div>
            <button
              onClick={() => onTooltipShow(trafficMetrics.title, trafficMetrics.tooltip)}
              className="text-gray-400 hover:text-blue-500"
            >
              <i className="ri-information-line text-sm"></i>
            </button>
          </div>

          {summaryError ? (
            <div className="flex flex-col items-center justify-center py-6 text-center">
              <i className="ri-error-warning-line text-3xl text-red-400 mb-2"></i>
              <p className="text-sm text-red-500 font-medium">Failed to load data from server</p>
            </div>
          ) : !connectionsLoaded || (loading && !summaryError) ? (
            <div className="flex flex-col items-center justify-center py-6 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500 mb-2"></div>
              <p className="text-sm text-gray-500 dark:text-gray-400">Loading data…</p>
            </div>
          ) : !gscConnected ? (
            <div className="flex flex-col items-center justify-center py-6 text-center">
              <i className="ri-bar-chart-line text-3xl text-gray-300 dark:text-gray-600 mb-2"></i>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {!ga4Connected ? 'Connect GA and GSC to fetch the data' : 'Connect GSC to fetch traffic data'}
              </p>
            </div>
          ) : trafficMetrics.data_available ? (
            <>
              <div className="grid grid-cols-3 gap-3 mb-4">
                <div className="text-center">
                  <p className="text-2xl font-bold text-teal-600">
                    {trafficMetrics.totalClicks >= 1000000
                      ? `${(trafficMetrics.totalClicks / 1000000).toFixed(1)}M`
                      : trafficMetrics.totalClicks >= 1000
                        ? `${(trafficMetrics.totalClicks / 1000).toFixed(1)}K`
                        : trafficMetrics.totalClicks}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Clicks</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-gray-900 dark:text-white">
                    {trafficMetrics.totalImpressions >= 1000000
                      ? `${(trafficMetrics.totalImpressions / 1000000).toFixed(1)}M`
                      : trafficMetrics.totalImpressions >= 1000
                        ? `${(trafficMetrics.totalImpressions / 1000).toFixed(1)}K`
                        : trafficMetrics.totalImpressions}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Impressions</p>
                </div>
                <div className="text-center">
                  <p
                    className={`text-2xl font-bold ${trafficMetrics.avgDuration && trafficMetrics.avgDuration !== 'N/A' ? 'text-gray-900 dark:text-white' : 'text-gray-400 dark:text-gray-500'}`}
                    title="Average duration from connected analytics data"
                  >
                    {trafficMetrics.avgDuration || 'N/A'}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Avg Duration</p>
                </div>
              </div>

              <div className="space-y-2">
                <p className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">Top Performing Pages</p>
                {trafficMetrics.topPages.slice(0, 5).map((page: any, idx: number) => (
                  <div key={idx} className="flex items-center justify-between text-sm">
                    <span className="text-gray-900 dark:text-white truncate flex-1" title={page.page === '/' ? `${siteDomain}/` : `${siteDomain}${page.page}`}>
                      {page.page === '/' ? `${siteDomain}/` : `${siteDomain}${page.page}`}
                    </span>
                    <span className="font-bold text-gray-900 dark:text-white ml-2">
                      {page.visitors >= 1000
                        ? `${(page.visitors / 1000).toFixed(1)}K`
                        : page.visitors}
                    </span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center py-6 text-center">
              <i className="ri-bar-chart-line text-3xl text-gray-300 dark:text-gray-600 mb-2"></i>
              <p className="text-sm text-gray-500 dark:text-gray-400">No traffic data yet</p>
            </div>
          )}
        </div>

        {/* Competitor Benchmark Card - Interactive */}
        <div
          onClick={handleCompetitorCardClick}
          className="bg-white dark:bg-gray-800 rounded-2xl p-5 shadow-sm border border-gray-100 dark:border-gray-700 cursor-pointer hover:shadow-md transition-shadow group"
        >
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 rounded-xl bg-orange-500 flex items-center justify-center group-hover:scale-110 transition-transform">
                <i className="ri-bar-chart-box-line text-white text-xl"></i>
              </div>
              <div>
                <h3 className="text-base font-bold text-gray-900 dark:text-white">Competitive<br />Position</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400">Market standing</p>
              </div>
            </div>
          </div>

          <div className="card-content">
            <div className="competitor-preview text-center py-4">
              <p className="text-sm font-medium text-gray-900 dark:text-white mb-1">Click to view detailed analysis</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">Perceived vs Actual Competitors</p>
            </div>
          </div>
        </div>

        {/* Competitor Analysis Modal */}
        <CompetitorAnalysisModal
          isOpen={isCompetitorModalOpen}
          onClose={() => setIsCompetitorModalOpen(false)}
          userId={localStorage.getItem('user_id') || ''}
        />
      </div>

      {/* Performance Filter & Tab Navigation */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Performance Filter</h3>
          <div className="flex items-center space-x-2">
            {[
              { id: 'optimal', label: 'Optimal', icon: 'ri-checkbox-circle-line' },
              { id: 'needs-attention', label: 'Needs Attention', icon: 'ri-error-warning-line' },
              { id: 'full', label: 'Full View', icon: 'ri-eye-line' }
            ].map((filter) => (
              <button
                key={filter.id}
                onClick={() => setPerformanceFilter(filter.id)}
                className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-all duration-200 ${performanceFilter === filter.id
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
              >
                <i className={`${filter.icon} text-sm`}></i>
                <span className="text-sm font-medium whitespace-nowrap">{filter.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex space-x-1 bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
          {[
            { id: 'onpage', label: 'On-Page SEO', icon: 'ri-file-text-line', count: onPageCategories.length },
            { id: 'offpage', label: 'Off-Page SEO', icon: 'ri-links-line', count: offPageCategories.length },
            { id: 'technical', label: 'Core Web Vitals & Technical SEO', icon: 'ri-speed-line', count: technicalCategories.length }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-lg transition-all duration-200 ${activeTab === tab.id
                ? 'bg-white dark:bg-gray-600 text-blue-600 dark:text-blue-400 shadow-sm'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                }`}
            >
              <i className={`${tab.icon} text-lg`}></i>
              <span className="font-medium whitespace-nowrap">{tab.label}</span>
              <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${activeTab === tab.id
                ? 'bg-blue-100 text-blue-600'
                : 'bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-400'
                }`}>
                {tab.count}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* On-Page SEO Categories */}
      {
        activeTab === 'onpage' && (
          <div className="space-y-4">
            {getFilteredCategories(onPageCategories).map((category) => {
              const isExpanded = expandedCategories.includes(category.id);
              const categoryStatus = getCategoryStatus(category.parameters);

              return (
                <div key={category.id} className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                  {/* Category Header */}
                  <div
                    className="p-6 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors"
                    onClick={() => toggleCategory(category.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4 flex-1">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${categoryStatus === 'optimal'
                          ? 'bg-green-100 dark:bg-green-900/30'
                          : categoryStatus === 'critical'
                            ? 'bg-red-100 dark:bg-red-900/30'
                            : 'bg-yellow-100 dark:bg-yellow-900/30'
                          }`}>
                          <i className={`${category.icon} text-xl ${categoryStatus === 'optimal'
                            ? 'text-green-600'
                            : categoryStatus === 'critical'
                              ? 'text-red-600'
                              : 'text-yellow-600'
                            }`}></i>
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center space-x-2">
                            <h3 className="text-lg font-bold text-gray-900 dark:text-white">{category.name}</h3>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                onTooltipShow(category.name, category.tooltip);
                              }}
                              className="text-gray-400 hover:text-blue-500"
                            >
                              <i className="ri-information-line text-sm"></i>
                            </button>
                          </div>
                          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                            {category.parameters.length} parameters tracked
                          </p>
                          {category.id === 'page-experience' && (
                            <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                              INP requires real user traffic data. Not available for low-traffic sites.
                            </p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center space-x-4">
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${categoryStatus === 'optimal'
                          ? 'bg-green-100 text-green-700'
                          : 'bg-yellow-100 text-yellow-700'
                          }`}>
                          {categoryStatus === 'optimal' ? 'Optimal' : 'Needs Attention'}
                        </span>
                        <i className={`ri-arrow-${isExpanded ? 'up' : 'down'}-s-line text-2xl text-gray-400`}></i>
                      </div>
                    </div>
                  </div>

                  {/* Expandable Parameters */}
                  {isExpanded && (
                    <div className="border-t border-gray-200 dark:border-gray-700">
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead className="bg-gray-50 dark:bg-gray-700">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Parameter</th>
                              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Current State</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                            {category.parameters.map((param: any, idx: number) => (
                              <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                                <td className="px-6 py-4">
                                  <span className="text-sm font-medium text-gray-900 dark:text-white">{param.name}</span>
                                </td>
                                <td className="px-6 py-4 text-center">
                                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${param.statusType === 'optimal'
                                    ? 'bg-green-100 text-green-700'
                                    : param.statusType === 'critical'
                                      ? 'bg-red-100 text-red-700'
                                      : 'bg-yellow-100 text-yellow-700'
                                    }`}>
                                    <span className={`w-2 h-2 rounded-full mr-2 ${param.statusType === 'optimal'
                                      ? 'bg-green-500'
                                      : param.statusType === 'critical'
                                        ? 'bg-red-500'
                                        : 'bg-yellow-500'
                                      }`}></span>
                                    {param.status}
                                  </span>
                                </td>
                                <td className="px-6 py-4">
                                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                                    {param.currentState}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )
      }

      {/* Off-Page SEO Categories */}
      {
        activeTab === 'offpage' && (
          <div className="space-y-4">
            {getFilteredCategories(offPageCategories).map((category) => {
              const isExpanded = expandedCategories.includes(category.id);
              const categoryStatus = getCategoryStatus(category.parameters);

              return (
                <div key={category.id} className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                  <div
                    className="p-6 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors"
                    onClick={() => toggleCategory(category.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4 flex-1">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${categoryStatus === 'optimal'
                          ? 'bg-green-100 dark:bg-green-900/30'
                          : 'bg-yellow-100 dark:bg-yellow-900/30'
                          }`}>
                          <i className={`${category.icon} text-xl ${categoryStatus === 'optimal'
                            ? 'text-green-600'
                            : 'text-yellow-600'
                            }`}></i>
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center space-x-2">
                            <h3 className="text-lg font-bold text-gray-900 dark:text-white">{category.name}</h3>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                onTooltipShow(category.name, category.tooltip);
                              }}
                              className="text-gray-400 hover:text-blue-500"
                            >
                              <i className="ri-information-line text-sm"></i>
                            </button>
                          </div>
                          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                            {category.parameters.length} parameters tracked
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-4">
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${categoryStatus === 'optimal'
                          ? 'bg-green-100 text-green-700'
                          : 'bg-yellow-100 text-yellow-700'
                          }`}>
                          {categoryStatus === 'optimal' ? 'Optimal' : 'Needs Attention'}
                        </span>
                        <i className={`ri-arrow-${isExpanded ? 'up' : 'down'}-s-line text-2xl text-gray-400`}></i>
                      </div>
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="border-t border-gray-200 dark:border-gray-700">
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead className="bg-gray-50 dark:bg-gray-700">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Parameter</th>
                              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Current State</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                            {category.parameters.map((param: any, idx: number) => (
                              <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                                <td className="px-6 py-4">
                                  <div className="flex items-center space-x-2">
                                    <span className="text-sm font-medium text-gray-900 dark:text-white">{param.name}</span>
                                    {param.info && (
                                      <button
                                        onClick={() => onTooltipShow(param.name, param.info)}
                                        className="text-gray-400 hover:text-blue-500"
                                      >
                                        <i className="ri-information-line text-xs"></i>
                                      </button>
                                    )}
                                  </div>
                                </td>
                                <td className="px-6 py-4 text-center">
                                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${param.statusType === 'optimal'
                                    ? 'bg-green-100 text-green-700'
                                    : 'bg-yellow-100 text-yellow-700'
                                    }`}>
                                    <span className={`w-2 h-2 rounded-full mr-2 ${param.statusType === 'optimal'
                                      ? 'bg-green-500'
                                      : 'bg-yellow-500'
                                      }`}></span>
                                    {param.status}
                                  </span>
                                </td>
                                <td className="px-6 py-4">
                                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                                    {param.currentState}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )
      }

      {/* Technical SEO Categories - Updated with status text only */}
      {
        activeTab === 'technical' && (
          <div className="space-y-4">
            {getFilteredTechnicalCategories(technicalCategories).map((category) => {
              const isExpanded = expandedCategories.includes(category.id);
              const categoryStatus = getTechnicalCategoryStatus(category.parameters);

              return (
                <div key={category.id} className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                  <div
                    className="p-6 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors"
                    onClick={() => toggleCategory(category.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4 flex-1">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${categoryStatus === 'optimal'
                          ? 'bg-green-100 dark:bg-green-900/30'
                          : categoryStatus === 'critical'
                            ? 'bg-red-100 dark:bg-red-900/30'
                            : 'bg-yellow-100 dark:bg-yellow-900/30'
                          }`}>
                          <i className={`${category.icon} text-xl ${categoryStatus === 'optimal'
                            ? 'text-green-600'
                            : categoryStatus === 'critical'
                              ? 'text-red-600'
                              : 'text-yellow-600'
                            }`}></i>
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center space-x-2">
                            <h3 className="text-lg font-bold text-gray-900 dark:text-white">{category.name}</h3>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                onTooltipShow(category.name, category.tooltip);
                              }}
                              className="text-gray-400 hover:text-blue-500"
                            >
                              <i className="ri-information-line text-sm"></i>
                            </button>
                          </div>
                          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                            {category.parameters.length} parameters tracked
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-4">
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${categoryStatus === 'optimal'
                          ? 'bg-green-100 text-green-700'
                          : 'bg-yellow-100 text-yellow-700'
                          }`}>
                          {categoryStatus === 'optimal' ? 'Optimal' : 'Needs Attention'}
                        </span>
                        <i className={`ri-arrow-${isExpanded ? 'up' : 'down'}-s-line text-2xl text-gray-400`}></i>
                      </div>
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="border-t border-gray-200 dark:border-gray-700">
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead className="bg-gray-50 dark:bg-gray-700">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Parameter</th>
                              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Current State</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                            {category.parameters.map((param: any, idx: number) => (
                              <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                                <td className="px-6 py-4">
                                  <div className="flex items-center space-x-2">
                                    <span className="text-sm font-medium text-gray-900 dark:text-white">{param.name}</span>
                                    {param.info && (
                                      <button
                                        onClick={() => onTooltipShow(param.name, param.info)}
                                        className="text-gray-400 hover:text-blue-500"
                                      >
                                        <i className="ri-information-line text-xs"></i>
                                      </button>
                                    )}
                                  </div>
                                </td>
                                <td className="px-6 py-4 text-center">
                                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${param.statusType === 'optimal'
                                    ? 'bg-green-100 text-green-700'
                                    : param.statusType === 'critical'
                                      ? 'bg-red-100 text-red-700'
                                      : 'bg-yellow-100 text-yellow-700'
                                    }`}>
                                    <span className={`w-2 h-2 rounded-full mr-2 ${param.statusType === 'optimal'
                                      ? 'bg-green-500'
                                      : param.statusType === 'critical'
                                        ? 'bg-red-500'
                                        : 'bg-yellow-500'
                                      }`}></span>
                                    {param.status}
                                  </span>
                                </td>
                                <td className="px-6 py-4">
                                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                                    {param.currentState}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )
      }

      {/* Next Step CTA */}
      <div className="bg-gradient-to-r from-purple-500 to-blue-600 rounded-xl p-6 shadow-lg">
        <div className="flex items-center justify-between text-white">
          <div>
            <h3 className="text-xl font-bold mb-2">Ready to Plan and Implement Your SEO Strategy?</h3>
            <p className="text-purple-100">Based on your baseline assessment, let's define achievable SMART goals</p>
          </div>
          <button
            onClick={() => navigate('/dashboard#goal-setting')}
            className="px-6 py-3 bg-white text-purple-600 rounded-lg font-semibold hover:bg-purple-50 transition-colors whitespace-nowrap"
          >
            Set Goals →
          </button>
        </div>
      </div>

      {/* Popups */}
      <DetailPopup
        isOpen={isKeywordPopupOpen}
        onClose={() => setIsKeywordPopupOpen(false)}
        title="Keyword Rankings (Tracked Keywords)"
      >
        <div className="space-y-6">
          {/* Informational Sections */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Metric</p>
              <p className="text-sm text-gray-900 dark:text-white font-medium">Keyword Rankings</p>
            </div>
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Type</p>
              <p className="text-sm text-gray-900 dark:text-white font-medium">Position/Numeric</p>
            </div>
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Goal</p>
              <p className="text-sm text-gray-900 dark:text-white font-medium">Rank in Top 10</p>
            </div>
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Source & Insight</p>
              <p className="text-sm text-gray-900 dark:text-white font-medium">From Keyword Universe + GSC/SERP Top 100 positions</p>
            </div>
          </div>

          {/* Rankings Table Section */}
          <div className="pt-4 border-t border-gray-100 dark:border-gray-700">
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4">Featuring Keywords (Top 100 from Keyword Universe)</h4>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-900/50">
                  <tr>
                    <th scope="col" className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Keyword</th>
                    <th scope="col" className="px-4 py-2 text-right text-xs font-semibold text-gray-500 uppercase">Position</th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-100 dark:divide-gray-700 font-medium">
                  {keywordMetrics.topKeywords.map((itemValue: any, idx: number) => {
                    const isNearMe = String(itemValue.keyword || '').toLowerCase().includes('near me');
                    const isNA = isNearMe || itemValue.position === 'N/A' || itemValue.position === 'NA';
                    return (
                      <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100 italic">
                          {itemValue.keyword}
                        </td>
                        <td className="px-4 py-3 text-sm text-right">
                          <span className={`font-bold ${
                            isNA ? 'text-gray-400'
                              : itemValue.isTop ? 'text-teal-600'
                              : /^\d+(\.\d+)?$/.test(String(itemValue.position)) ? 'text-gray-600'
                              : 'text-gray-400'
                          }`}>
                            {isNA ? 'N/A' : /^\d+(\.\d+)?$/.test(String(itemValue.position)) ? `#${itemValue.position}` : itemValue.position}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </DetailPopup>

      <DetailPopup
        isOpen={isBlogPopupOpen}
        onClose={() => setIsBlogPopupOpen(false)}
        title="Organic Traffic"
      >
        <div className="space-y-6">
          {/* Informational Sections */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Metric</p>
              <p className="text-sm text-gray-900 dark:text-white font-medium">Organic Traffic</p>
            </div>
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Type</p>
              <p className="text-sm text-gray-900 dark:text-white font-medium">Numeric</p>
            </div>
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Goal</p>
              <p className="text-sm text-gray-900 dark:text-white font-medium">Measure site visits</p>
            </div>
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Source & Insight</p>
              <p className="text-sm text-gray-900 dark:text-white font-medium">From Google Search Console; informs content ROI</p>
            </div>
          </div>

          {/* Top Performing Pages Section */}
          <div className="pt-4 border-t border-gray-100 dark:border-gray-700">
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4">Top Performing Pages</h4>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-900/50">
                  <tr>
                    <th scope="col" className="px-4 py-2 text-left text-xs font-semibold text-gray-500 uppercase">Page</th>
                    <th scope="col" className="px-4 py-2 text-right text-xs font-semibold text-gray-500 uppercase">Clicks</th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-100 dark:divide-gray-700 font-medium">
                  {trafficMetrics.topPages.slice(0, 5).length > 0 ? (
                    trafficMetrics.topPages.slice(0, 5).map((page: any, idx: number) => (
                      <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                        <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100 truncate max-w-[250px]" title={page.page === '/' ? `${siteDomain}/` : `${siteDomain}${page.page}`}>
                          {page.page === '/' ? `${siteDomain}/` : `${siteDomain}${page.page}`}
                        </td>
                        <td className="px-4 py-3 text-sm text-right text-gray-900 dark:text-gray-100">
                          {page.visitors >= 1000 ? `${(page.visitors / 1000).toFixed(1)}K` : page.visitors}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={2} className="px-4 py-6 text-center text-sm text-gray-400 italic">
                        No traffic data available for individual pages.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Vocabulary Helper Section */}
        </div>
      </DetailPopup>

      {/* SERP Positioning Popup */}
      <DetailPopup
        isOpen={isSerpPopupOpen}
        onClose={() => { setIsSerpPopupOpen(false); setSerpTab('features'); }}
        title="Captured Visibility (Tracked Keywords)"
      >
        <div className="flex flex-col h-full overflow-hidden">
          {/* Informational Sections */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-xl border border-purple-100 dark:border-purple-800/30">
              <h4 className="text-sm font-bold text-purple-900 dark:text-purple-300 mb-1 flex items-center">
                <i className="ri-layout-grid-line mr-2"></i> Metric Definition
              </h4>
              <p className="text-xs text-purple-800 dark:text-purple-400 leading-relaxed">
                <strong>Metric</strong>: SERP Feature Visibility<br />
                <strong>Type</strong>: Brand Presence &amp; Feature Dominance<br />
                <strong>Goal</strong>: Own high-visibility &quot;0th&quot; positions (snippets, panels) the user's attention.
              </p>
            </div>
            <div className="bg-indigo-50 dark:bg-indigo-900/20 p-4 rounded-xl border border-indigo-100 dark:border-indigo-800/30">
              <h4 className="text-sm font-bold text-indigo-900 dark:text-indigo-300 mb-1 flex items-center">
                <i className="ri-lightbulb-line mr-2"></i> Source &amp; Insight
              </h4>
              <p className="text-xs text-indigo-800 dark:text-indigo-400 leading-relaxed">
                <strong>Source</strong>: Live Google SERP Analysis (Top 100 Results)<br />
                <strong>Insight</strong>: Beyond standard rankings, these features represent &quot;real estate&quot; on the search result page. Occupying them increases CTR and brand authority.
              </p>
            </div>
          </div>

          {/* BRD: Tab navigation — Feature Overview and Keyword Mapping */}
          <div className="flex border-b border-gray-200 dark:border-gray-700 mb-4">
            <button
              id="serp-tab-features"
              onClick={() => setSerpTab('features')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                serpTab === 'features'
                  ? 'border-purple-500 text-purple-600 dark:text-purple-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              <i className="ri-table-line mr-1"></i> Feature Overview
            </button>
            <button
              id="serp-tab-keywords"
              onClick={() => setSerpTab('keywords')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                serpTab === 'keywords'
                  ? 'border-purple-500 text-purple-600 dark:text-purple-400'
                  : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              <i className="ri-key-line mr-1"></i> Keyword Mapping
              <span className="ml-1 px-1.5 py-0.5 rounded-full bg-purple-100 dark:bg-purple-900/40 text-purple-600 dark:text-purple-400 text-xs font-semibold">
                {serpMetrics.keywordDetails?.length ?? 0}
              </span>
            </button>
          </div>

          {/* Tab 1: Feature Overview */}
          {serpTab === 'features' && (() => {
            const featureRows = serpMetrics.features || [];
            const organicRows = featureRows.filter((f: any) => !f.paid);
            const paidRows = featureRows.filter((f: any) => f.paid);
            return (
              <div className="mb-4">
                <h4 className="text-sm font-bold text-gray-900 dark:text-white mb-2 flex items-center">
                  <i className="ri-table-line mr-2 text-purple-500"></i> Captured Visibility — Feature Brand Presence
                  {serpMetrics.data_available && (
                    <span className="ml-2 px-2 py-0.5 rounded-full bg-purple-100 dark:bg-purple-900/40 text-purple-600 dark:text-purple-400 text-xs font-semibold">
                      Applicable: {serpApplicableCount}
                    </span>
                  )}
                </h4>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                  Active = your domain appears in that SERP feature for at least one tracked keyword. Missing = feature appeared in SERP but your domain was absent. Not Applicable = feature did not appear for any tracked keyword.
                </p>

                <div className="overflow-x-auto border border-gray-100 dark:border-gray-700 rounded-xl mb-4">
                  <div className="px-4 py-2 bg-emerald-50 dark:bg-emerald-900/20 border-b border-gray-100 dark:border-gray-700 text-xs font-semibold text-emerald-700 dark:text-emerald-300 uppercase tracking-wider">
                    Organic Features
                  </div>
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-900/50">
                      <tr>
                        <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">SERP Feature</th>
                        <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">What It Measures</th>
                        <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Type</th>
                        <th scope="col" className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Your Presence</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-100 dark:divide-gray-700">
                      {organicRows.map((feat: any) => {
                        return (
                          <tr key={feat.key} className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                            <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-gray-100">{feat.name}</td>
                            <td className="px-4 py-3 text-xs text-gray-600 dark:text-gray-300">{feat.description}</td>
                            <td className="px-4 py-3 text-xs text-gray-500 dark:text-gray-400">{feat.type}</td>
                            <td className="px-4 py-3 text-center">
                              {feat.present ? (
                                <span className="inline-flex items-center text-xs font-medium text-green-600">
                                  <i className="ri-checkbox-circle-fill mr-1"></i> Active
                                </span>
                              ) : feat.missing ? (
                                <span className="inline-flex items-center text-xs font-medium text-amber-600">
                                  <i className="ri-close-circle-line mr-1"></i> Missing
                                </span>
                              ) : (
                                <span className="inline-flex items-center text-xs font-medium text-gray-400">
                                  <i className="ri-subtract-line mr-1"></i> Not Applicable
                                </span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                      {organicRows.length === 0 && (
                        <tr>
                          <td colSpan={4} className="px-4 py-6 text-center text-sm text-gray-400 italic">
                            No SERP feature data available yet.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                <div className="overflow-x-auto border border-gray-100 dark:border-gray-700 rounded-xl">
                  <div className="px-4 py-2 bg-purple-50 dark:bg-purple-900/20 border-b border-gray-100 dark:border-gray-700 text-xs font-semibold text-purple-700 dark:text-purple-300 uppercase tracking-wider">
                    Paid Features
                  </div>
                  <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                    <thead className="bg-gray-50 dark:bg-gray-900/50">
                      <tr>
                        <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">SERP Feature</th>
                        <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">What It Measures</th>
                        <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Type</th>
                        <th scope="col" className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Your Presence</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-100 dark:divide-gray-700">
                      {paidRows.map((feat: any) => (
                        <tr key={feat.key} className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                          <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-gray-100">{feat.name}</td>
                          <td className="px-4 py-3 text-xs text-gray-600 dark:text-gray-300">{feat.description}</td>
                          <td className="px-4 py-3 text-xs text-gray-500 dark:text-gray-400">{feat.type}</td>
                          <td className="px-4 py-3 text-center">
                            {feat.present ? (
                              <span className="inline-flex items-center text-xs font-medium text-green-600">
                                <i className="ri-checkbox-circle-fill mr-1"></i> Active
                              </span>
                            ) : feat.missing ? (
                              <span className="inline-flex items-center text-xs font-medium text-amber-600">
                                <i className="ri-close-circle-line mr-1"></i> Missing
                              </span>
                            ) : (
                              <span className="inline-flex items-center text-xs font-medium text-gray-400">
                                <i className="ri-subtract-line mr-1"></i> Not detected
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })()}

          {/* Tab 2: Keyword Mapping — BRD: Keyword → Applicable SERP Feature → Brand Presence/Absence */}
          {serpTab === 'keywords' && (() => {
            const kwDetails: any[] = serpMetrics.keywordDetails || [];
            return (
              <div>
                <h4 className="text-sm font-bold text-gray-900 dark:text-white mb-1 flex items-center">
                  <i className="ri-key-line mr-2 text-purple-500"></i> Keyword → SERP Feature → Brand Presence
                </h4>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                  Shows every locked keyword and which search features Google detected for it. Your brand's presence or absence in each feature is indicated.
                </p>
                {kwDetails.length === 0 ? (
                  <div className="py-10 text-center text-sm text-gray-400 italic">
                    No keyword mapping data available. Trigger a SERP re-scan from the AS-IS card.
                  </div>
                ) : (
                  <div className="overflow-x-auto border border-gray-100 dark:border-gray-700 rounded-xl">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                      <thead className="bg-gray-50 dark:bg-gray-900/50">
                        <tr>
                          <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">#</th>
                          <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Locked Keyword</th>
                          <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">SERP Features Found</th>
                          <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Your Brand Presence</th>
                          <th scope="col" className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Rank</th>
                          <th scope="col" className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Top 10</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-100 dark:divide-gray-700">
                        {kwDetails.map((row: any, idx: number) => {
                          const features: string[] = row.features || [];
                          const yourPresence: string[] = row.your_presence || [];
                          const hasFeaturesFound = features.length > 0;
                          const hasBrandPresence = yourPresence.length > 0;
                          return (
                            <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                              <td className="px-4 py-3 text-xs text-gray-400 dark:text-gray-500">{idx + 1}</td>
                              <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-gray-100 max-w-[200px]">
                                <span className="break-words">{row.keyword}</span>
                              </td>
                              <td className="px-4 py-3">
                                {hasFeaturesFound ? (
                                  <div className="flex flex-wrap gap-1">
                                    {features.map((f: string, fi: number) => (
                                      <span key={fi} className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 font-medium">
                                        {f}
                                      </span>
                                    ))}
                                  </div>
                                ) : (
                                  <span className="text-xs text-gray-400 italic">No special features</span>
                                )}
                              </td>
                              <td className="px-4 py-3">
                                {hasFeaturesFound ? (
                                  hasBrandPresence ? (
                                    <div className="flex flex-wrap gap-1">
                                      {yourPresence.map((f: string, fi: number) => (
                                        <span key={fi} className="inline-flex items-center px-1.5 py-0.5 rounded text-xs bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300 font-medium">
                                          <i className="ri-checkbox-circle-fill mr-0.5"></i>{f}
                                        </span>
                                      ))}
                                    </div>
                                  ) : (
                                    <span className="inline-flex items-center text-xs font-medium text-amber-600">
                                      <i className="ri-close-circle-line mr-1"></i> Not present
                                    </span>
                                  )
                                ) : (
                                  /* BRD §9: No SERP features detected for this keyword → clearly label as Not Visible */
                                  <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400">
                                    <i className="ri-eye-off-line"></i> Not Visible
                                  </span>
                                )}
                              </td>
                              <td className="px-4 py-3 text-center text-sm font-semibold text-gray-700 dark:text-gray-300">
                                {row.position != null ? `#${row.position}` : '—'}
                              </td>
                              <td className="px-4 py-3 text-center">
                                {row.is_top10 ? (
                                  <span className="inline-flex items-center text-xs font-medium text-green-600">
                                    <i className="ri-checkbox-circle-fill"></i>
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center text-xs font-medium text-gray-400">
                                    <i className="ri-subtract-line"></i>
                                  </span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      </DetailPopup>
    </div>
  );
}
